import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../services/api';
import { createApiUrlWithPrefix } from '../config/api';
import {
  Users,
  UserCheck,
  UserX,
  UserPlus,
  RefreshCw,
  Activity,
  Shield,
  Crown,
  Zap,
  User2,
  PieChart as PieChartIcon,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  AreaChart,
  Area
} from 'recharts';

function UserReports() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    deactivatedUsers: 0,
    newUsersThisMonth: 0,
    newRidersThisMonth: 0,
    newCoachesThisMonth: 0,
    activeRiders: [],
    activeCoaches: [],
    inactiveRiders: [],
    inactiveCoaches: [],
    newRidersThisMonthList: [],
    newCoachesThisMonthList: [],
    usersByRole: {},
    monthlyGrowth: [],
    roleDistribution: [],
    statusDistribution: [],
    recentActivity: [],
    usersByAgeGroup: []
  });

  const [cotisation, setCotisation] = useState({
    loading: false,
    totalMembers: 0,
    activeForfaitRiders: 0,
    payingRiders: 0,
    subscribedOwners: 0,
    members: []
  });

  const isRTL = i18n.language === 'ar';

  const fetchUserReportData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiEndpoints.users.getAll({ limit: 1000 });
      const users = response.data.items || response.data.users || response.data;
      
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      const filteredUsers = users.filter(user => user.role !== 4); // Exclude SuperAdmin
      
      const totalUsers = filteredUsers.length;
      const activeUsers = filteredUsers.filter(user => user.status === 0).length;
      const deactivatedUsers = filteredUsers.filter(user => user.status === 1).length;
      
      const newUsersThisMonth = filteredUsers.filter(user => {
        const userDate = new Date(user.createdAt);
        return userDate.getMonth() === currentMonth && userDate.getFullYear() === currentYear;
      }).length;

      const isRider = (user) => (typeof user.role === 'string' ? parseInt(user.role) : user.role) === 0;
      const isCoach = (user) => (typeof user.role === 'string' ? parseInt(user.role) : user.role) === 2;
      const isActiveUser = (user) => user.status === 0;

      // New riders & coaches added this month
      const newRidersThisMonthList = filteredUsers.filter(u => isRider(u) && new Date(u.createdAt).getMonth() === currentMonth && new Date(u.createdAt).getFullYear() === currentYear);
      const newCoachesThisMonthList = filteredUsers.filter(u => isCoach(u) && new Date(u.createdAt).getMonth() === currentMonth && new Date(u.createdAt).getFullYear() === currentYear);

      // Active vs not active riders & coaches (current club status)
      const activeRiders = filteredUsers.filter(u => isRider(u) && isActiveUser(u));
      const activeCoaches = filteredUsers.filter(u => isCoach(u) && isActiveUser(u));
      const inactiveRiders = filteredUsers.filter(u => isRider(u) && !isActiveUser(u));
      const inactiveCoaches = filteredUsers.filter(u => isCoach(u) && !isActiveUser(u));

      // Role distribution
      const roleCounts = filteredUsers.reduce((acc, user) => {
        const roleNum = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const roleName = t(`roles.${roleNum}`);
        acc[roleName] = (acc[roleName] || 0) + 1;
        return acc;
      }, {});

      const roleDistribution = Object.entries(roleCounts).map(([name, value]) => ({ name, value }));

      // Monthly growth
      const monthlyGrowth = Array.from({ length: 6 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const month = date.getMonth();
        const year = date.getFullYear();
        
        const count = filteredUsers.filter(user => {
          const userDate = new Date(user.createdAt);
          return userDate.getMonth() === month && userDate.getFullYear() === year;
        }).length;
        
        return {
          name: date.toLocaleDateString(i18n.language, { month: 'short' }),
          users: count
        };
      }).reverse();

      // Age distribution
      const calculateAge = (birthDate) => {
        if (!birthDate) return null;
        const birth = new Date(birthDate);
        let age = now.getFullYear() - birth.getFullYear();
        if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) {
          age--;
        }
        return age;
      };

      const ageGroups = filteredUsers.reduce((acc, user) => {
        const age = calculateAge(user.birthDate);
        if (age === null || isNaN(age)) acc[t('demographics.notSpecified')]++;
        else if (age <= 12) acc[t('demographics.kids')]++;
        else if (age <= 17) acc[t('demographics.juniors')]++;
        else if (age <= 50) acc[t('demographics.adults')]++;
        else acc[t('demographics.seniors')]++;
        return acc;
      }, {
        [t('demographics.kids')]: 0,
        [t('demographics.juniors')]: 0,
        [t('demographics.adults')]: 0,
        [t('demographics.seniors')]: 0,
        [t('demographics.notSpecified')]: 0
      });

      const usersByAgeGroup = Object.entries(ageGroups).map(([name, value]) => ({ name, value }));

      const recentActivity = filteredUsers
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 8)
        .map(user => ({
          id: user.id,
          name: user.name || `${user.firstName} ${user.lastName}`,
          roleNum: typeof user.role === 'string' ? parseInt(user.role) : user.role,
          status: user.status,
          createdAt: new Date(user.createdAt),
          email: user.email
        }));

      setUserStats({
        totalUsers,
        activeUsers,
        deactivatedUsers,
        newUsersThisMonth,
        newRidersThisMonth: newRidersThisMonthList.length,
        newCoachesThisMonth: newCoachesThisMonthList.length,
        activeRiders,
        activeCoaches,
        inactiveRiders,
        inactiveCoaches,
        newRidersThisMonthList,
        newCoachesThisMonthList,
        roleDistribution,
        monthlyGrowth,
        recentActivity,
        usersByAgeGroup
      });

    } catch (error) {
      handleApiError(error, 'Failed to load user statistics');
    } finally {
      setLoading(false);
    }
  }, [i18n.language, t]);

  useEffect(() => {
    fetchUserReportData();
  }, [fetchUserReportData]);

  const fetchCotisation = useCallback(async () => {
    try {
      setCotisation(prev => ({ ...prev, loading: true }));
      const response = await axios.get(createApiUrlWithPrefix('finance-reports/active-cotisation'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = response.data;
      setCotisation({
        loading: false,
        totalMembers: data.totalMembers || 0,
        activeForfaitRiders: data.activeForfaitRiders || 0,
        payingRiders: data.payingRiders || 0,
        subscribedOwners: data.subscribedOwners || 0,
        members: data.members || []
      });
    } catch (error) {
      console.error('Failed to load cotisation data:', error);
      setCotisation(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchCotisation();
  }, [fetchCotisation]);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];

  const getRoleIcon = (roleNum) => {
    switch (roleNum) {
      case 4: return Crown;
      case 3: return Shield;
      case 2: return UserCheck;
      case 1: return Zap;
      case 5: return User2;
      default: return Users;
    }
  };

  const getRoleColor = (roleNum) => {
    switch (roleNum) {
      case 4: return 'text-rose-600 bg-rose-50';
      case 3: return 'text-purple-600 bg-purple-50';
      case 2: return 'text-blue-600 bg-blue-50';
      case 1: return 'text-emerald-600 bg-emerald-50';
      case 0: return 'text-amber-600 bg-amber-50';
      case 5: return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading && !userStats.totalUsers) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <RefreshCw className="w-12 h-12 text-purple-500 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 pb-20 ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.tabs.users')}</h1>
          <p className="text-gray-500">{t('reports.userAnalytics.subtitle')}</p>
        </div>
        <button
          onClick={() => { fetchUserReportData(); fetchCotisation(); }}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-all text-gray-700 text-sm font-medium w-fit"
        >
          <RefreshCw className={`w-4 h-4 ${loading || cotisation.loading ? 'animate-spin' : ''}`} />
          <span>{t('common.refresh')}</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title={t('reports.totalUsers')}
          value={userStats.totalUsers}
          icon={<Users className="text-blue-500" />}
          bg="bg-blue-50"
          color="text-blue-700"
        />
        <KpiCard
          title={t('reports.activeUsers')}
          value={userStats.activeUsers}
          icon={<UserCheck className="text-emerald-500" />}
          bg="bg-emerald-50"
          color="text-emerald-700"
          subtitle={`${Math.round((userStats.activeUsers / (userStats.totalUsers || 1)) * 100)}% ${t('reports.ofTotal')}`}
        />
        <KpiCard
          title={t('reports.deactivated')}
          value={userStats.deactivatedUsers}
          icon={<UserX className="text-rose-500" />}
          bg="bg-rose-50"
          color="text-rose-700"
          subtitle={`${Math.round((userStats.deactivatedUsers / (userStats.totalUsers || 1)) * 100)}% ${t('reports.ofTotal')}`}
        />
        <KpiCard
          title={t('reports.newThisMonth')}
          value={userStats.newUsersThisMonth}
          icon={<UserPlus className="text-purple-500" />}
          bg="bg-purple-50"
          color="text-purple-700"
        />
        <KpiCard
          title={t('reports.newRidersThisMonth')}
          value={userStats.newRidersThisMonth}
          icon={<Users className="text-amber-500" />}
          bg="bg-amber-50"
          color="text-amber-700"
        />
        <KpiCard
          title={t('reports.newCoachesThisMonth')}
          value={userStats.newCoachesThisMonth}
          icon={<UserCheck className="text-sky-500" />}
          bg="bg-sky-50"
          color="text-sky-700"
        />
      </div>

      {/* New Riders & Coaches This Month */}
      {userStats.newRidersThisMonth + userStats.newCoachesThisMonth > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">{t('reports.newRidersThisMonth')}</h3>
              <span className="text-xs font-black px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">{userStats.newRidersThisMonth}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {userStats.newRidersThisMonthList.length === 0 ? (
                <p className="p-5 text-sm text-gray-400">{t('reports.noData')}</p>
              ) : (
                userStats.newRidersThisMonthList.map((rider) => (
                  <MemberRow key={rider.id} user={rider} i18n={i18n} statusLabel />
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">{t('reports.newCoachesThisMonth')}</h3>
              <span className="text-xs font-black px-2.5 py-1 rounded-full bg-sky-100 text-sky-700">{userStats.newCoachesThisMonth}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {userStats.newCoachesThisMonthList.length === 0 ? (
                <p className="p-5 text-sm text-gray-400">{t('reports.noData')}</p>
              ) : (
                userStats.newCoachesThisMonthList.map((coach) => (
                  <MemberRow key={coach.id} user={coach} i18n={i18n} statusLabel />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Club Status - Active vs Not Active This Month */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800">{t('reports.activeMembers')}</h3>
            <span className="text-xs font-black px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
              {userStats.activeRiders.length + userStats.activeCoaches.length}
            </span>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {userStats.activeRiders.map((rider) => (
              <MemberRow key={rider.id} user={rider} i18n={i18n} label={t('reports.rider')} />
            ))}
            {userStats.activeCoaches.map((coach) => (
              <MemberRow key={coach.id} user={coach} i18n={i18n} label={t('reports.coach')} />
            ))}
            {userStats.activeRiders.length + userStats.activeCoaches.length === 0 && (
              <p className="p-5 text-sm text-gray-400">{t('reports.noData')}</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800">{t('reports.inactiveMembers')}</h3>
            <span className="text-xs font-black px-2.5 py-1 rounded-full bg-rose-100 text-rose-700">
              {userStats.inactiveRiders.length + userStats.inactiveCoaches.length}
            </span>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {userStats.inactiveRiders.map((rider) => (
              <MemberRow key={rider.id} user={rider} i18n={i18n} label={t('reports.rider')} />
            ))}
            {userStats.inactiveCoaches.map((coach) => (
              <MemberRow key={coach.id} user={coach} i18n={i18n} label={t('reports.coach')} />
            ))}
            {userStats.inactiveRiders.length + userStats.inactiveCoaches.length === 0 && (
              <p className="p-5 text-sm text-gray-400">{t('reports.noData')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Active Cotisation - Paid This Month */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-gray-800">💳 {t('reports.activeCotisation')}</h3>
            <span className="text-xs font-black px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">{new Date().toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">{t('reports.cotisationTotal')}: {cotisation.totalMembers}</span>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">🏇 {t('reports.forfaitRiders')}: {cotisation.activeForfaitRiders}</span>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700">💵 {t('reports.payRiders')}: {cotisation.payingRiders}</span>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700">🐴 {t('reports.subscribedOwners')}: {cotisation.subscribedOwners}</span>
          </div>
        </div>

        {cotisation.loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
            <p className="text-sm text-gray-500 font-medium">{t('common.loading')}</p>
          </div>
        ) : cotisation.members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <span className="text-4xl mb-3">💳</span>
            <p className="text-lg font-medium">{t('reports.noCotisation')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/70 text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-6 py-3 font-bold">{t('reports.member')}</th>
                  <th className="px-6 py-3 font-bold">{t('reports.role')}</th>
                  <th className="px-6 py-3 font-bold">{t('reports.category')}</th>
                  <th className="px-6 py-3 font-bold text-right">{t('reports.amount')}</th>
                  <th className="px-6 py-3 font-bold">{t('reports.detail')}</th>
                  <th className="px-6 py-3 font-bold text-right">{t('reports.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {cotisation.members.map((m, idx) => {
                  const roleLabel = m.role === 'owner' ? t('reports.owner') : t('reports.rider');
                  const categoryLabel = m.category === 'forfait'
                    ? t('reports.catForfait')
                    : m.category === 'payment'
                      ? t('reports.catPayment')
                      : t('reports.catSubscription');
                  const statusLabel = m.status === 'active' ? t('common.active') : t('common.deactivated');
                  let detailLabel = '';
                  if (m.category === 'forfait') {
                    detailLabel = t('reports.detailLessons', { count: m.lessonsRemaining });
                  } else if (m.category === 'payment') {
                    detailLabel = t('reports.detailPayments', { count: m.paymentCount });
                  } else if (m.category === 'subscription') {
                    detailLabel = t('reports.detailExpires', { date: new Date(m.expiryDate).toLocaleDateString(i18n.language) });
                  }
                  return (
                    <tr key={`${m.userId}-${idx}`} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center text-emerald-700 font-bold text-sm shrink-0">
                            {(m.name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{m.name}</p>
                            {m.email && <p className="text-xs text-gray-400">{m.email}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                          m.role === 'owner' ? 'bg-violet-50 text-violet-600 border-violet-100' : 'bg-amber-50 text-amber-600 border-amber-100'
                        }`}>
                          {roleLabel}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-gray-600">{categoryLabel}</td>
                      <td className="px-6 py-3 text-right font-bold text-gray-800">{Number(m.amount).toLocaleString()} DA</td>
                      <td className="px-6 py-3 text-gray-500">{detailLabel}</td>
                      <td className="px-6 py-3 text-right">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                          m.status === 'active' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'
                        }`}>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Growth Chart */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">{t('reports.monthlyGrowth')}</h3>
            <TrendingUp className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userStats.monthlyGrowth}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} />
                <Tooltip />
                <Area type="monotone" dataKey="users" name={t('reports.tabs.users')} stroke="#8B5CF6" strokeWidth={3} fillOpacity={1} fill="url(#colorUsers)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Role Distribution */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">{t('reports.usersByRole')}</h3>
            <PieChartIcon className="w-5 h-5 text-blue-500" />
          </div>
          <div className="h-[250px] w-full mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={userStats.roleDistribution}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {userStats.roleDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {userStats.roleDistribution.map((role, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50/50">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                <span className="text-[11px] font-bold text-gray-600 truncate">{role.name}</span>
                <span className="text-[11px] font-black text-gray-400 ms-auto">{role.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800">{t('reports.recentUserActivity')}</h3>
            <Activity className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="divide-y divide-gray-50">
            {userStats.recentActivity.map((user) => {
              const Icon = getRoleIcon(user.roleNum);
              const colorClass = getRoleColor(user.roleNum);
              return (
                <div key={user.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="text-sm font-bold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                      {user.createdAt.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                    </p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                      user.status === 0 ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'
                    }`}>
                      {user.status === 0 ? t('common.active') : t('common.deactivated')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Age Demographics */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">{t('demographics.ageGroups')}</h3>
            <Users className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="h-[250px] w-full mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={userStats.usersByAgeGroup} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{fill: '#4B5563', fontSize: 11, fontWeight: 600}} width={100} />
                <Tooltip />
                <Bar dataKey="value" fill="#6366F1" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-3">
            {userStats.usersByAgeGroup.map((group, idx) => {
              const perc = Math.round((group.value / (userStats.totalUsers || 1)) * 100);
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between text-xs font-bold text-gray-500 uppercase tracking-tighter">
                    <span>{group.name}</span>
                    <span>{perc}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-indigo-500 h-full rounded-full transition-all duration-500" style={{ width: `${perc}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const KpiCard = ({ title, value, icon, bg, color, subtitle }) => (
  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{title}</span>
      <div className={`p-2 rounded-xl ${bg}`}>
        {React.cloneElement(icon, { size: 18 })}
      </div>
    </div>
    <div className="flex items-baseline gap-2">
      <span className={`text-2xl font-black ${color}`}>{value}</span>
      {subtitle && <span className="text-[10px] font-bold text-gray-400">{subtitle}</span>}
    </div>
  </div>
);

const MemberRow = ({ user, i18n, label, statusLabel }) => {
  const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
  return (
    <div className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center text-indigo-700 font-bold text-sm shrink-0">
        {name.charAt(0)?.toUpperCase() || 'U'}
      </div>
      <div className="flex-1 overflow-hidden">
        <p className="text-sm font-bold text-gray-900 truncate">{name}</p>
        <p className="text-xs text-gray-400 truncate">{user.email || label || ''}</p>
      </div>
      {statusLabel && (
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border shrink-0 ${
          user.status === 0 ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'
        }`}>
          {user.status === 0 ? i18n.t('common.active') : i18n.t('common.deactivated')}
        </span>
      )}
      {label && !statusLabel && (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold border shrink-0 bg-gray-50 text-gray-500 border-gray-100">
          {label}
        </span>
      )}
    </div>
  );
};

export default UserReports;

